import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SlidersHorizontal } from 'lucide-react';
import type { SettingToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useSettingsList, useSettingNames, useSites, useSettingMutations } from '~/hooks/use-settings';
import { tokenToForm, type SettingFormValues } from '~/lib/schemas/setting';
import { isKnownLoyaltySetting } from '~/lib/loyalty-settings';
import { SettingEditDialog } from './setting-edit-dialog';

/** Settings CRUD surface — parity with the legacy admin key-value grid (ParamName/Value/Site). */
export function SettingsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useSettingsList();
	const { data: names = [] } = useSettingNames();
	const { data: sites = [] } = useSites();
	const { remove } = useSettingMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<SettingFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<SettingToken | null>(null);

	const siteMap = useMemo(() => new Map(sites.map(s => [s.id, s.nm])), [sites]);
	const siteOf = (s: SettingToken) => (s.wsid == null ? t('system.settings.baseSite') : (siteMap.get(s.wsid) ?? `#${s.wsid}`));

	const columns = useMemo<SystemGridColumn<SettingToken>[]>(
		() => [
			{
				key: 'name',
				header: t('system.settings.columns.name'),
				width: '260px',
				sortValue: s => s.pn,
				exportValue: s => s.pn,
				render: s => (
					<span className="truncate font-mono text-xs font-medium text-foreground" title={s.pn}>
						{s.pn}
					</span>
				)
			},
			{
				key: 'value',
				header: t('system.settings.columns.value'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.pv ?? '',
				exportValue: s => s.pv ?? '',
				render: s => (
					<span className="truncate text-sm text-foreground-muted" title={s.pv ?? ''}>
						{s.pv}
					</span>
				)
			},
			{
				key: 'site',
				header: t('system.settings.columns.site'),
				width: '160px',
				sortValue: s => siteOf(s),
				exportValue: s => siteOf(s),
				render: s =>
					s.wsid == null ? (
						<span className="truncate text-sm text-foreground-subtle">{t('system.settings.baseSite')}</span>
					) : (
						<span className="truncate text-sm text-foreground-muted">{siteMap.get(s.wsid) ?? `#${s.wsid}`}</span>
					)
			},
			{
				key: 'group',
				header: t('system.fields.paramGroup'),
				width: '160px',
				defaultVisible: false,
				sortValue: s => s.prmg ?? '',
				exportValue: s => s.prmg ?? '',
				render: s => <span className="truncate text-sm text-foreground-muted">{s.prmg || '—'}</span>
			},
			{
				key: 'description',
				header: t('system.fields.description'),
				width: '220px',
				defaultVisible: false,
				sortValue: s => s.descr ?? '',
				exportValue: s => s.descr ?? '',
				render: s => (
					<span className="truncate text-sm text-foreground-muted" title={s.descr ?? ''}>
						{s.descr || '—'}
					</span>
				)
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, siteMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SettingToken>
				gridId="settings"
				title={t('system.settings.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? s.pn}
				columns={columns}
				searchPredicate={(s, q) => `${s.pn ?? ''} ${s.pv ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<SlidersHorizontal size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.settings.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={s => {
					setEditing(tokenToForm(s));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				canDelete={s => !isKnownLoyaltySetting(s.pn)}
				exportFilename="settings"
			/>

			<SettingEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} names={names} sites={sites} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.settings.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.settings.deleteBody', { name: deleteRow?.pn ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
